// src/pages/HomePage.jsx
import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import "../../../styles/HomePage.css";
import { useLocation, useNavigate } from "react-router-dom";
import centerLogo from "../../../assets/icons/logo.svg";
import { FaStop, FaCheckCircle } from "react-icons/fa";
import AudioIcon from "../../../assets/icons/audio.js";
import VideoIcon from "../../../assets/icons/video.js";
import FullScreenChat from "../components/FullScreenChat.jsx";
import { useWebSocketChat } from "../../../socket/useSocket.jsx";
import { ReactComponent as MicIcon } from "../../../assets/icons/mic.svg";
import SendIcon from "../../../assets/icons/send.svg";
import aipsychologistIcon from "../../../assets/icons/aipsychologist.svg";
import aidieticianIcon from "../../../assets/icons/aidietician.svg";
import { AuthServices } from "../../auth/services/AuthServices.js";
import { OverlayService } from "../service/OverlayService.js";
import insightsIcon from "../../../assets/icons/insights.svg";
import SlideOver from "../components/SlideOver";




function HomePage() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const prashaBotResponseRef = useRef([]);
  const audioChunksRef = useRef([]);
  const textareaRef = useRef(null);

  // UI state
  const [modalType, setModalType] = useState(null);
  const [showModelSelect, setShowModelSelect] = useState(false);
  const [chatMode, setChatMode] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingExpert, setPendingExpert] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [activeChat, setActiveChat] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [sessionState, setSessionState] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isOverflowHome, setIsOverflowHome] = useState(false);

  const InsightsPanel = lazy(() => import("../components/InsightsPanel"));
  const JournalPanel = lazy(() => import("../components/JournalPanel"));


  const [panel, setPanel] = useState(null);
  const openPanel = (key) => setPanel(key);
  const closePanel = () => setPanel(null);


  // expert / model selection
  const [selectedExpert, setSelectedExpert] = useState(() => {
    return localStorage.getItem("selectedExpert") || "psychologist";
  });
  const [selectedModel, setSelectedModel] = useState("psychologist");

  // input and user
  const [userInput, setUserInput] = useState(""); // keep controlled from first render
  const [user, setUser] = useState(() => {
    // support both keys used across your files
    const u = localStorage.getItem("user");
    try { return u ? JSON.parse(u) : null; } catch { return u || null; }
  });

  const [date] = useState(
    new Date().toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedChat = searchParams.get("chat");

  // hook: support both versions’ API surface
  const {
    sendMessage,
    socket,
    updatePersona,
    updatePatientId,
    updateConversationId, // may be undefined in one version
    connectWebSocket,      // may be undefined in one version
    disconnectWebSocket,   // may be undefined in one version
  } = useWebSocketChat({
    setPatientInfo: () => { },
    setCompleteAudio: () => { },
    onStreamingStart: (data) => handleStreamingStart(data),
    onStreamingText: (data) => handleIncomingStreamingText(data),
    onStreamingAudio: (data) => handleStreamAudio(data),
    onStreamingComplete: (data) => handleStreamingComplete(data),
    onStreamingError: (data) => handleStreamError(data),
    onTranscription: (data) => handleTranscription(data),
    onConversationHistory: () => { },
  });

  const handleSelectExpert = (role) => {
    setSelectedExpert(role);
    updatePersona?.(role);
    if (socket && socket.readyState === 1) {
      socket.close();
    }
  };

  const toggleRecording = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      alert("Audio recording is not supported in this browser.");
      return;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      console.warn("Already recording.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      mediaRecorder.onstop = () => {
        sendAudio();
        stream.getTracks().forEach((track) => track.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Microphone error:", error);
      alert(`Microphone error: ${error.message}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleTranscription = (data) => {
    setUserInput(data || "");
    setShowTyping(false);
  };

  // ===== AUTOGROW / AUTOSCROLL (from file 1, enhanced) =====
  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = "auto";
    const cs = getComputedStyle(el);
    const maxHStr = cs.maxHeight; // "240px" or "none"
    const maxPx = maxHStr !== "none" ? parseFloat(maxHStr) : Infinity;

    const newH = Math.min(el.scrollHeight, maxPx);
    el.style.height = `${newH}px`;

    setIsOverflowHome(el.scrollHeight > newH + 1);
  };

  useEffect(() => {
    // run after paint for accurate scrollHeight
    requestAnimationFrame(autoResize);
  }, [userInput, showTyping]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const handleAutoScroll = () => {
      el.scrollTop = el.scrollHeight;
    };
    el.addEventListener("input", handleAutoScroll);
    return () => el.removeEventListener("input", handleAutoScroll);
  }, []);
  // ===== END AUTOGROW / AUTOSCROLL =====

  const addMessage = (text, sender, keywords = [], audio = null) => {
    const newMessage = {
      id: Date.now(),
      text,
      sender,
      timestamp: new Date(),
      isStreaming: false,
      keywords: keywords || [],
      audio,
      isPlaying: false,
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const handleActivateFullScreen = (e) => {
    if (e.key === "Enter" && !e.shiftKey && userInput.trim() !== "") {
      addMessage(userInput, "user");
      // prefer selectedModel if set, fallback to expert
      const model = (selectedModel || selectedExpert).toLowerCase();
      sendMessage(userInput, model);
      setChatMode(true);
      setTimeout(() => setChatVisible(true), 400);
      setUserInput("");
      const el = textareaRef.current;
      if (el) el.style.height = "auto";
      setIsOverflowHome(false);
    }
  };

  const handleIncomingStreamingText = (partialText) => {
    setMessages((prevMessages) => {
      const lastIndex = prevMessages.length - 1;
      const lastMessage = prevMessages[lastIndex];

      if (lastMessage && lastMessage.sender === "ai" && lastMessage.isStreaming) {
        const updatedMessage = {
          ...lastMessage,
          text: (lastMessage.text + " " + partialText).replace(/\s+/g, " "),
        };
        const updatedMessages = [...prevMessages];
        updatedMessages[lastIndex] = updatedMessage;
        return updatedMessages;
      }

      const newMessage = {
        id: Date.now(),
        text: partialText,
        sender: "ai",
        timestamp: new Date(),
        isStreaming: true,
        keywords: [],
        audio: null,
        isPlaying: false,
      };
      return [...prevMessages, newMessage];
    });
  };

  const handleModalOpen = (type) => setModalType(type);
  const handleModalClose = () => setModalType(null);

  const handleStreamAudio = (data) => {
    // space-safe join like file 1
    const currentChunk = data.text;
    const lastChunk = prashaBotResponseRef.current.at(-1) || "";
    const prevChar = lastChunk.slice(-1);
    const firstChar = currentChunk.charAt(0);
    const needsSpace =
      lastChunk.length > 0 &&
      !/\s/.test(prevChar) &&
      !/^[.,!?;:'")\]]/.test(firstChar) &&
      !/^\s/.test(firstChar);
    prashaBotResponseRef.current.push(needsSpace ? ` ${currentChunk}` : currentChunk);

    const text = prashaBotResponseRef.current.join("");
    setMessages((prev) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (lastIndex >= 0 && updated[lastIndex].sender === "ai" && updated[lastIndex].isStreaming) {
        updated[lastIndex] = { ...updated[lastIndex], text };
      }
      return updated;
    });
  };

  const handleModalConfirm = () => {
    if (!selectedExpert) {
      alert("Please select either AI Psychologist or AI Dietician first.");
      return;
    }
    const route = modalType === "video" ? "/vsession" : "/session";
    const modelString = selectedExpert === "psychologist" ? "ai psychologist" : "ai dietician";
    navigate(route, {
      state: { sessionType: modalType, selectedExpert, modelString },
    });
  };

  const handleStreamingStart = () => {
    setIsLoading(true);
    const newMessage = {
      id: Date.now(),
      text: "",
      sender: "ai",
      timestamp: new Date(),
      isStreaming: true,
      keywords: [],
      audio: null,
      isPlaying: false,
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const handleStreamingComplete = () => {
    setMessages((prev) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (lastIndex >= 0 && updated[lastIndex].sender === "ai" && updated[lastIndex].isStreaming) {
        updated[lastIndex] = { ...updated[lastIndex], isStreaming: false };
      }
      return updated;
    });
    prashaBotResponseRef.current = [];
    setIsLoading(false);
  };

  const handleStreamError = (data) => {
    console.log(data);
  };

  const handleSend = () => {
    if (!userInput?.trim()) return;
    addMessage(userInput, "user");
    const model = (selectedModel || selectedExpert).toLowerCase();
    sendMessage(userInput, model);
    setUserInput("");
    const el = textareaRef.current;
    if (el) el.style.height = "auto";
    setIsOverflowHome(false);
    setChatMode(true);
    setTimeout(() => setChatVisible(true), 400);
  };

  const sendAudio = () => {
    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    audioChunksRef.current = [];
    const reader = new FileReader();
    setShowTyping(true);
    reader.onload = () => {
      const base64Audio = reader.result.split(",")[1];
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ audio: base64Audio }));
        console.log("Audio sent to server");
      } else {
        console.error("WebSocket not connected");
        alert("WebSocket not connected. Please refresh the page.");
        setShowTyping(false);
      }
    };
    reader.readAsDataURL(audioBlob);
  };

  const fetchUserData = async () => {
    try {
      const response = await AuthServices.fetchUserData();
      console.log(response.data)
      const fetchedUser = response?.data?.data?.user;

      if (response?.status && response.status !== 200) {
        localStorage.clear();
        navigate("/");
      }

      if (fetchedUser) {
        setUser(fetchedUser);
        localStorage.setItem("user", JSON.stringify(fetchedUser));
        if (fetchedUser.patient_id) {
          updatePatientId?.(fetchedUser.patient_id);
        }
        // connect websocket if available in this hook variant
        if (connectWebSocket) connectWebSocket();
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  const fetch = async (sessionId) => {
    const response = await OverlayService.fetchConversation(sessionId);
    setChatHistory(response.data);
    // align persona with first message receiver like file 1
    if (response.data?.messages?.[0]?.receiver_id === process.env.REACT_APP_PSYCHOLOGIST) {
      updatePersona?.("psychologist");
      setSelectedExpert("psychologist");
    } else {
      updatePersona?.("dietician");
      setSelectedExpert("dietician");
    }
  };

  const renderChatHistory = () => setChatVisible(true);

  // prefill when opened from a saved chat
  useEffect(() => {
    if (selectedChat) {
      setMessages([
        { role: "expert", text: `You opened: "${selectedChat}"` },
        { role: "user", text: "Thanks! Let's continue..." },
      ]);
      setActiveChat(selectedChat);
      setShowChat(true);
    }
  }, [selectedChat]);

  // URL param session id
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get("session");
    if (sessionId) {
      setSessionState(sessionId);
      fetch(sessionId);
    } else {
      console.log("No session ID in URL");
    }
  }, [location.search]);

  useEffect(() => {
    if (sessionState) renderChatHistory();
  }, [sessionState]);

  // persona persistence and localStorage
  useEffect(() => {
    if (user && user.patient_id) {
      updatePersona?.(selectedExpert);
    }
  }, [user, selectedExpert]);

  useEffect(() => {
    if (selectedExpert) localStorage.setItem("selectedExpert", selectedExpert);
  }, [selectedExpert]);

  // initial user + optional WS connect
  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <div className="home-container">
      {!chatVisible ? (
        <div
          className={`home-main-content ${chatMode ? "fade-out" : "fade-in"}`}
          style={{ display: chatVisible ? "none" : "block" }}
        >
          {/* Greeting and Logo */}
          <div className="greeting">
            <p className="date">{date && date}</p>
            <h2 className="username">
              Welcome{" "}
              {user?.first_name
                ? user?.first_name ||
                JSON.parse(localStorage.getItem("user") || "{}")?.first_name?.split(" ")?.[0]
                : "Guest"}
              !
            </h2>
          </div>

          <div className="center-logo-wrapper">
            <img src={centerLogo} alt="Center Logo" className="center-logo" />
          </div>

          {/* Main content area with scrolling */}
          <div className="chat-content-area">
            {/* This area is intentionally left empty for future messages or content */}
          </div>

          {/* Fixed bottom container for input and controls */}
          <div className="chat-bottom-container">
            {/* Input Box */}
            <div className="input-box">
              <textarea
                ref={textareaRef}
                value={showTyping ? "Transcribing..." : userInput}
                disabled={showTyping}
                onChange={(e) => {
                  setUserInput(e.target.value);
                  requestAnimationFrame(autoResize);
                }}
                onKeyDown={(e) => handleActivateFullScreen(e)}
                className={`chat-textarea ${isOverflowHome ? "is-scrolling" : ""}`}
                placeholder={`Ask Your ${selectedExpert}`}
                rows={1}
              />
            </div>

            <div className="chat-controls-row">
              {/* Psychologist (white bg) */}
              <button
                className={`icon-btn icon-btn--white ${selectedExpert === "psychologist" ? "active" : ""}`}
                onClick={() => handleSelectExpert("psychologist")}
                aria-label="AI Psychologist"
              >
                <img src={aipsychologistIcon} alt="Psychologist" width={24} height={24} />
                <span className="icon-label">Doctor</span>
              </button>

              {/* Dietician (white bg) */}
              <button
                className={`icon-btn icon-btn--white ${selectedExpert === "dietician" ? "active" : ""}`}
                onClick={() => handleSelectExpert("dietician")}
                aria-label="AI Dietician"
              >
                <img src={aidieticianIcon} alt="Dietician" width={24} height={24} />
                <span className="icon-label">Dietician</span>
              </button>

              {/* Insights (white bg) */}
              <button
                className="icon-btn icon-btn--white"
                onClick={() => openPanel("insights")}
                aria-label="Insights"
              >
                <img src={insightsIcon} alt="Insights" width={24} height={24} />
                <span className="icon-label">Insights</span>
              </button>

              {/* Mic (purple bg + white icon) */}
              <button
                onClick={toggleRecording}
                disabled={showTyping}
                className="icon-btn icon-btn--white"
                aria-label={isRecording ? "Stop recording" : "Start recording"}
              >
                {isRecording ? <FaStop /> : <MicIcon />}
              </button>

              {/* Audio (purple bg + white icon) */}
              <button
                className="icon-btn icon-btn--primary"
                onClick={() => handleModalOpen("audio")}
                aria-label="Audio"
              >
                <AudioIcon width={20} height={20} />
              </button>

              {/* Video (purple bg + white icon) */}
              <button
                className="icon-btn icon-btn--primary"
                onClick={() => handleModalOpen("video")}
                aria-label="Video"
              >
                <VideoIcon width={20} height={20} />
              </button>

              {/* Send (separate, always visible; enabled only if there’s text) */}
              <button
                className="icon-btn icon-btn--primary send-btn"
                onClick={handleSend}
                disabled={!userInput || !userInput.trim()}
                aria-label="Send"
              >
                <img src={SendIcon} alt="Send" width={20} height={20} />
              </button>
            </div>

          </div>
        </div>
      ) : (
        <FullScreenChat
          handleModalOpen={handleModalOpen}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          messages={messages}
          addMessage={addMessage}
          sendMessage={sendMessage}
          messagesEndRef={messagesEndRef}
          userInput={userInput}
          setUserInput={setUserInput}
          autoResize={autoResize}
          setShowConfirmation={setShowConfirmation}
          selectedExpert={selectedExpert}
          toggleRecording={toggleRecording}
          showTyping={showTyping}
          isRecording={isRecording}
          chatHistory={chatHistory}
        />
      )}



      {/* Confirmation Modal */}
      {modalType && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3 className="modal-title">Confirmation</h3>
            <p className="modal-text">
              To start a {modalType} session with <strong>{selectedExpert}</strong>?
            </p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={handleModalClose}>
                Cancel
              </button>
              <button className="start-btn" onClick={handleModalConfirm}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      {showModelSelect && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-drag-indicator" />
            <h3 className="modal-title">Choose Your AI Model</h3>
            <div className="model-options">
              {["AI Psychologist", "AI Dietician"].map((model) => (
                <div
                  key={model}
                  className={`model-option ${selectedModel === model ? "selected" : ""}`}
                  onClick={() => setSelectedModel(model)}
                >
                  <span>{model}</span>
                  <input type="radio" checked={selectedModel === model} readOnly />
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowModelSelect(false)}>
                Cancel
              </button>
              <button
                className="start-btn"
                disabled={!selectedModel}
                onClick={() =>
                  navigate("/session", {
                    state: { selectedModel, selectedExpert, sessionType: modalType },
                  })
                }
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      <SlideOver
        title={
          panel === "insights"
            ? "Insights"
            : panel === "mood"
              ? "Mood"
              : panel === "journal"
                ? "Journal"
                : ""
        }
        isOpen={!!panel}
        onClose={closePanel}
      >
        {panel && (
          <Suspense fallback={<div>Loading…</div>}>
            {panel === "insights" && <InsightsPanel onClose={closePanel} />}
            {panel === "journal" && <JournalPanel onClose={closePanel} />}
            {/* add Mood or other panels here in the same way */}
          </Suspense>
        )}
      </SlideOver>


      {showConfirmation && (
        <div className="chat-confirmation-overlay">
          <div className="chat-confirmation-box">
            <h4>Confirmation</h4>
            <p>Ending current session. Starting session with the {pendingExpert}.</p>
            <div className="chat-confirmation-buttons">
              <button
                className="outline-btn"
                onClick={() => {
                  setSelectedExpert(pendingExpert);
                  setUserInput("");
                  setShowConfirmation(false);
                }}
              >
                Start New
              </button>
              <button className="filled-btn" onClick={() => setShowConfirmation(false)}>
                Stay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}

export default HomePage;
